import { type ReactNode } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import type { AvatarData } from '@/api';
import { ArrowTurnUprightOutlinedIcon, LoginIcon, PenPaperIcon, PersonStackIcon, PlusIcon, TrashIcon } from '@/icons';
import { cn } from '@/utils';
import { ClassroomDropdownMenu, ClassroomDropdownMenuItem, ClassroomDropdownMenuSeparator, ClassroomDropdownMenuTrigger } from '../ClassroomDropdownMenu';
import { ClassroomAvatarPair } from './ClassroomAvatarPair';
export type ClassroomItem = {
  id: string;
  name: string;
  subtitle: string;
  ownerAvatarData?: AvatarData;
  memberCount: number;
  isOwner: boolean;
  href: string;
};
type ClassroomCardMenuItem = {
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  separatorBefore?: boolean;
};
function ClassroomCardFrame({
  children,
  href,
  onClick,
  className
}: {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  className?: string;
}) {
  return <Link href={href ?? '#'} onClick={onClick} className={cn('flex h-[164px] w-full cursor-pointer flex-col gap-md overflow-hidden rounded-md border border-alternative bg-default p-lg shadow-xs transition-all hover:border-opacity hover:shadow-md', className)} data-sentry-element="Link" data-sentry-component="ClassroomCardFrame" data-sentry-source-file="ClassroomCards.tsx">
      {children}
    </Link>;
}
function ClassroomCardMenu({
  items
}: {
  items: ClassroomCardMenuItem[];
}) {
  return <ClassroomDropdownMenu trigger={<ClassroomDropdownMenuTrigger preventDefault />} data-sentry-element="ClassroomDropdownMenu" data-sentry-component="ClassroomCardMenu" data-sentry-source-file="ClassroomCards.tsx">
      {items.map(item => <div key={item.label}>
          {item.separatorBefore ? <ClassroomDropdownMenuSeparator /> : null}
          <ClassroomDropdownMenuItem icon={item.icon} onSelect={item.onSelect}>
            {item.label}
          </ClassroomDropdownMenuItem>
        </div>)}
    </ClassroomDropdownMenu>;
}
function ClassroomCardTopSection({
  item,
  menuItems
}: {
  item: ClassroomItem;
  menuItems: ClassroomCardMenuItem[];
}) {
  return <div className="flex w-full items-start justify-between py-3xs" data-sentry-component="ClassroomCardTopSection" data-sentry-source-file="ClassroomCards.tsx">
      <ClassroomAvatarPair classroomId={item.id} classroomName={item.name} ownerAvatarData={item.ownerAvatarData} data-sentry-element="ClassroomAvatarPair" data-sentry-source-file="ClassroomCards.tsx" />
      {menuItems.length > 0 ? <ClassroomCardMenu items={menuItems} /> : null}
    </div>;
}
function ClassroomCardDetails({
  item
}: {
  item: ClassroomItem;
}) {
  return <div className="flex min-w-0 flex-col gap-3xs" data-sentry-component="ClassroomCardDetails" data-sentry-source-file="ClassroomCards.tsx">
      <h3 className="truncate text-heading-sm-bold text-default">
        {item.name}
      </h3>
      <div className="flex min-w-0 items-center gap-2xs">
        <span className="min-w-0 shrink truncate text-body-md-medium text-alternative">
          {item.subtitle}
        </span>
        <span className="inline-block size-[4px] shrink-0 rounded-full bg-[#CACBD8]" />
        <span className="inline-flex min-h-[22px] shrink-0 items-center gap-4xs rounded-full border border-alternative bg-default px-2xs">
          <PersonStackIcon width={12} height={12} fill={ICON_COLOR.neutral} data-sentry-element="PersonStackIcon" data-sentry-source-file="ClassroomCards.tsx" />
          <span className="text-caption-md-semibold text-neutral">
            {item.memberCount}
          </span>
        </span>
      </div>
    </div>;
}
export function OwnerClassroomCard({
  item,
  onClick,
  onDelete,
  onEditName,
  onShare,
  className
}: {
  item: ClassroomItem;
  onClick?: () => void;
  onDelete?: (item: ClassroomItem) => void;
  onEditName?: (item: ClassroomItem) => void;
  onShare?: (item: ClassroomItem) => void;
  className?: string;
}) {
  const {
    t
  } = useTranslation(['classroom']);
  const menuItems: ClassroomCardMenuItem[] = [...(onShare ? [{
    label: t('classroom:classroomCard.menu.invite'),
    icon: <ArrowTurnUprightOutlinedIcon size="sm" fill={ICON_COLOR.neutral} className="shrink-0" />,
    onSelect: () => onShare(item)
  }] : []), ...(onEditName ? [{
    label: t('classroom:classroomCard.menu.editName'),
    icon: <PenPaperIcon size="sm" fill={ICON_COLOR.neutral} className="shrink-0" />,
    onSelect: () => onEditName(item)
  }] : []), ...(onDelete ? [{
    label: t('classroom:classroomCard.menu.delete'),
    icon: <TrashIcon size="sm" fill={ICON_COLOR.neutral} className="shrink-0" />,
    onSelect: () => onDelete(item),
    separatorBefore: true
  }] : [])];
  return <ClassroomCardFrame href={item.href} onClick={onClick} className={className} data-sentry-element="ClassroomCardFrame" data-sentry-component="OwnerClassroomCard" data-sentry-source-file="ClassroomCards.tsx">
      <ClassroomCardTopSection item={item} menuItems={menuItems} data-sentry-element="ClassroomCardTopSection" data-sentry-source-file="ClassroomCards.tsx" />
      <ClassroomCardDetails item={item} data-sentry-element="ClassroomCardDetails" data-sentry-source-file="ClassroomCards.tsx" />
    </ClassroomCardFrame>;
}
export function StudentClassroomCard({
  item,
  onClick,
  className
}: {
  item: ClassroomItem;
  onClick?: () => void;
  className?: string;
}) {
  return <ClassroomCardFrame href={item.href} onClick={onClick} className={className} data-sentry-element="ClassroomCardFrame" data-sentry-component="StudentClassroomCard" data-sentry-source-file="ClassroomCards.tsx">
      <div className="flex w-full items-start justify-between py-3xs">
        <ClassroomAvatarPair classroomId={item.id} classroomName={item.name} ownerAvatarData={item.ownerAvatarData} data-sentry-element="ClassroomAvatarPair" data-sentry-source-file="ClassroomCards.tsx" />
      </div>
      <ClassroomCardDetails item={item} data-sentry-element="ClassroomCardDetails" data-sentry-source-file="ClassroomCards.tsx" />
    </ClassroomCardFrame>;
}
function ClassroomActionCard({
  icon,
  label,
  onClick,
  className
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  className?: string;
}) {
  return <div onClick={onClick} className={cn('flex h-[143px] w-full cursor-pointer flex-col items-center justify-center gap-sm overflow-hidden rounded-md border border-assistive bg-neutral px-lg py-2xl transition-colors hover:bg-alternative mobile-lg:h-[164px] mobile-lg:gap-md mobile-lg:py-3xl', className)} data-sentry-component="ClassroomActionCard" data-sentry-source-file="ClassroomCards.tsx">
      <div className="flex size-[28px] items-center justify-center">{icon}</div>
      <p className="text-body-lg-semibold text-alternative">{label}</p>
    </div>;
}
export function CreateClassroomCard({
  onClick
}: {
  onClick: () => void;
}) {
  const {
    t
  } = useTranslation(['classroom']);
  return <ClassroomActionCard icon={<PlusIcon size="lg" fill={ICON_COLOR.assistive} />} label={t('classroom:classroomCard.createClassroom')} onClick={onClick} data-sentry-element="ClassroomActionCard" data-sentry-component="CreateClassroomCard" data-sentry-source-file="ClassroomCards.tsx" />;
}
export function EnterClassroomCard({
  onClick
}: {
  onClick: () => void;
}) {
  const {
    t
  } = useTranslation(['classroom']);
  return <ClassroomActionCard icon={<LoginIcon size="lg" fill={ICON_COLOR.assistive} />} label={t('classroom:classroomCard.enterClassroom')} onClick={onClick} data-sentry-element="ClassroomActionCard" data-sentry-component="EnterClassroomCard" data-sentry-source-file="ClassroomCards.tsx" />;
}